'use client'

import React from 'react';
import { builder, BuilderContent } from "@builder.io/sdk";
import { useEffect, useState, useMemo, useCallback } from "react";
import { Skeleton } from '@/components/ui/skeleton';
import { SpacingType, sectionVariants } from '@/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';
import { PageContext } from '../builder';
import { useDebounce } from '@/hooks/useDebounce';
import { SectionWrapper } from '../components/SectionWraper';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { EmptyResult } from '../components/EmptyResult';
import Image from '@/components/ui/Image';
import parse from 'html-react-parser';
import { variantBackgroundColors } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface MarketInsightListingProps {  
  spacing?: SpacingType;
  darkMode?: boolean; 
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
}

interface TopicData {
  topic?: string;
}

interface BuilderQuery {
  'data.topics'?: {
    $elemMatch: {
      topic: string;
    };
  };
  $or?: Array<{
    name?: { $regex: string; $options: string };
    'data.title'?: { $regex: string; $options: string };
    'data.description'?: { $regex: string; $options: string };
    'data.topics.topic'?: { $regex: string; $options: string };
  }>;
}

// Memoized Controls component to prevent re-rendering when search results change
const Controls = React.memo<{
  selectedTopic: string;
  onTopicChange: (value: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  topicsData?: TopicData[];
  inputBackgroundColor: string;
}>(({ selectedTopic, onTopicChange, searchTerm, onSearchChange, topicsData, inputBackgroundColor }) => {
  // Use local state for input to prevent focus loss during parent re-renders
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  // const inputRef = useRef<HTMLInputElement>(null);
  
  // Sync local state with prop when it changes externally (e.g., reset)
  useEffect(() => {
    if (searchTerm !== localSearchTerm) {
      setLocalSearchTerm(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);
  
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    onSearchChange(value);
  }, [onSearchChange]);

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-10 justify-between py-8 md:py-20">
      <Select value={selectedTopic} onValueChange={onTopicChange}>
        <SelectTrigger className={cn(inputBackgroundColor, "w-full md:w-70")}>
          <SelectValue placeholder="All Topics" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Topics</SelectItem>
          {topicsData?.map((topic: TopicData) => (
            <SelectItem key={topic.topic} value={topic.topic ?? ""}>
              {topic.topic}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="relative">
        <Input 
          type="text" 
          placeholder="Search"  
          className={cn(inputBackgroundColor, "w-full md:w-80 pl-9")}
          value={localSearchTerm}
          onChange={handleInputChange}
        />
        <span className="material-symbols-outlined w-4.5 h-4.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ fontSize: '18px' }}>search</span>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent re-renders unless necessary props change
  // Note: We don't compare searchTerm here because it's handled internally
  return (
    prevProps.selectedTopic === nextProps.selectedTopic &&
    prevProps.inputBackgroundColor === nextProps.inputBackgroundColor &&
    prevProps.topicsData === nextProps.topicsData &&
    prevProps.onTopicChange === nextProps.onTopicChange &&
    prevProps.onSearchChange === nextProps.onSearchChange
  );
});

Controls.displayName = 'Controls';

// Memoized ArticleList component
const ArticleList = React.memo<{
  items: BuilderContent[];
  hasMore: boolean;
  isLoadingMore: boolean;
  isSearching?: boolean;
  onLoadMore: () => void;
  onReset: () => void;
}>(({ items, hasMore, isLoadingMore, isSearching, onLoadMore, onReset }) => {
  if (isSearching && items.length === 0) {
    return (
      <div className="grid gap-x-6 gap-y-8 md:gap-y-20 md:grid-cols-2">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="aspect-16/9 w-full bg-muted rounded-xl mb-4"></div>
            <div className="h-6 bg-muted rounded mb-2"></div>
            <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-muted rounded"></div>
          </div>
        ))}
      </div>
    );
  }
  
  if (items?.length > 0) {
    return (
      <>
        <div className="grid gap-x-6 gap-y-8 md:gap-y-20 md:grid-cols-2">
          {items.map((item: BuilderContent) => {
            const image = item.data?.image || "/placeholder.svg";
            const title = item.data?.title || "";
            const url = item.data?.url || "";
            const description = item.data?.description || "";
            const topics = item.data?.topics || [];
            return (
              <React.Fragment key={item.id}>
                <Card
                  key={item.id}
                  className="grid grid-rows-[auto_auto_1fr_auto] p-0 border-none gap-4"
                >
                  <div className="aspect-16/9 w-full pb-2 relative">
                    <a
                      href={url}
                      tabIndex={-1}
                      className="transition-opacity duration-200 fade-in hover:opacity-70 absolute inset-0 z-1 cursor-pointer"
                    >
                      <Image
                        src={image}
                        width={1000}
                        height={1000}
                        alt={item.data?.title ?? ""}  
                        title={item.data?.title ?? ""}
                        className="h-full w-full object-cover object-center rounded-xl z-1"
                      />
                      <div className="absolute inset-0 z-10">
                        <Image 
                          src="/gradient-mask.svg" 
                          alt="gradient" 
                          width={504} 
                          height={284} 
                          className="w-full h-full object-cover rounded-md" 
                        />
                      </div>
                    </a>
                  </div>
                  <CardHeader className="!p-0 gap-0">
                    <Link href={url} tabIndex={-1}>
                      <h4>
                        {parse(title)}
                      </h4>
                    </Link>
                  </CardHeader>
                  <CardContent className="!p-0 hidden sm:block">
                    <p className="text-muted-foreground">{description}</p>
                  </CardContent>
                  <CardFooter className="p-0 flex flex-wrap gap-2">
                    {topics && topics.length > 0 && (
                      topics.map((topic: {topic?: string}, index: number) => (
                        topic.topic && (
                          <Badge variant="secondary" key={index} bold>{topic.topic}</Badge>
                        )
                      ))
                    )}
                  </CardFooter>
                </Card>
              </React.Fragment>
            );
          })}
        </div>
        {hasMore && (
          <div className="mt-16 text-center">
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full md:w-auto cursor-pointer"
              onClick={onLoadMore}
              disabled={!hasMore || isLoadingMore}
            > 
              {isLoadingMore ? 'Loading...' : hasMore ? 'Load more articles' : 'No more articles'}
            </Button>
          </div>
        )}
      </>
    );
  } else {
    return (
      <div className="col-span-2">
        <EmptyResult
          className="mt-16"
          buttonOnClick={onReset}
        />
      </div>
    );
  }
});

ArticleList.displayName = 'ArticleList';

  export function MarketInsightListing({ spacing = 'xlarge', darkMode = false, variant = 'A', usedInPageNav = false, pageNavTitle = 'Market Insights', themeChangeOnScroll = false, attributes }: MarketInsightListingProps) {
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fetchedItems, setFetchedItems] = useState<BuilderContent[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [query, setQuery] = useState<BuilderQuery>({});
  const debouncedQuery = useDebounce(query, 500);

  const pageFields = React.useContext(PageContext);
  const { topicsData: rawTopicsData, articlesData, itemsPerPage } = (pageFields.data as unknown as { topicsData?: TopicData[]; articlesData?: BuilderContent[]; itemsPerPage?: number }) ?? {};
  
  // Memoize topicsData to prevent unnecessary re-renders of Controls
  const topicsData = useMemo(() => rawTopicsData, [rawTopicsData]);

  // Update query when filters or search changes
  useEffect(() => {
    const newQuery: BuilderQuery = {};

    if (selectedTopic && selectedTopic !== 'all') {
      newQuery['data.topics'] = {
        $elemMatch: {
          topic: selectedTopic
        }
      };
    }

    if (debouncedSearchTerm) {
      newQuery.$or = [
        { name: { $regex: debouncedSearchTerm, $options: 'i' } },
        { 'data.title': { $regex: debouncedSearchTerm, $options: 'i' } },
        { 'data.description': { $regex: debouncedSearchTerm, $options: 'i' } },
        { 'data.topics.topic': { $regex: debouncedSearchTerm, $options: 'i' } }
      ];
    }

    setQuery(newQuery);
  }, [selectedTopic, debouncedSearchTerm]);

  // Use debouncedQuery for data fetching
  const fetchFilteredData = React.useCallback(async () => {
    setIsSearching(true);
    setOffset(0); // Reset offset when query changes
    try {
      const articles = await builder.getAll('market-insights', {
        limit: (itemsPerPage ?? 0) + 1,
        query: debouncedQuery,
        options: {
          noTargeting: true,
        },
        preview: true,
        noTraverse: true,
        noEditorUpdates: true,
        prerender: false,
      });

      setFetchedItems(articles.slice(0, itemsPerPage ?? 0));
      setHasMore(articles.length > (itemsPerPage ?? 0));
      setIsSearching(false);
    } catch (err) {
      console.error("Filter fetch failed", err);
      setIsSearching(false);
    }
  }, [debouncedQuery, itemsPerPage]);
  
  const loadMore = React.useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const newOffset = offset + (itemsPerPage ?? 0);
      
      const newArticles = await builder.getAll('market-insights', {
        limit: (itemsPerPage ?? 0) + 1,
        offset: newOffset,
        query: debouncedQuery,
        options: {
          noTargeting: true,
        },
        preview: true,
        noTraverse: true,
        noEditorUpdates: true,
        prerender: false,
      });

      if (newArticles.length > 0) {
        setFetchedItems(prev => [...prev, ...newArticles.slice(0, itemsPerPage ?? 0)]);
        setOffset(newOffset);
        setHasMore(newArticles.length > (itemsPerPage ?? 0));
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Load more failed", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [offset, itemsPerPage, debouncedQuery]);

  // Initial data load and filter updates
  useEffect(() => {
    // If we have articlesData from context, use it for initial load
    if (articlesData && !debouncedQuery.$or && !debouncedQuery['data.topics']) {
      setFetchedItems(articlesData.slice(0, itemsPerPage ?? 0) || []);
      setHasMore(articlesData.length > (itemsPerPage ?? 0));
      setInitialLoading(false);
    } else {
      // Otherwise fetch with current filters
      if (initialLoading) {
        setInitialLoading(false);
      }
      fetchFilteredData();
    }
  }, [articlesData, itemsPerPage, debouncedQuery, fetchFilteredData, initialLoading]);

  useEffect(() => {
    if (fetchedItems?.length > 0 && initialLoading) {
      setInitialLoading(false);
    }
  }, [fetchedItems, initialLoading]);

  const inputBackgroundColor = useMemo(() => variantBackgroundColors(darkMode, variant), [darkMode, variant]);

  // Memoized handlers to prevent Controls from re-rendering
  const handleTopicChange = useCallback((value: string) => {
    setSelectedTopic(value);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleReset = useCallback(() => {
    setIsSearching(true);
    setSearchTerm("");
    setSelectedTopic("all");
    setQuery({});
  }, []);

  const handleLoadMore = useCallback(() => {
    loadMore();
  }, [loadMore]);

  if (initialLoading) {
    return (
      <SectionWrapper
        usedInPageNav={usedInPageNav}
        pageNavTitle={pageNavTitle}
        themeChangeOnScroll={themeChangeOnScroll}
        spacing={spacing}
        variant={variant}
        darkMode={darkMode}
        heroSection={false}
        componentName="case-studies-list"
        attributes={attributes}
      >
        <div className="container" data-aos="fade-up">
          <h2>
            Latest insights.
          </h2>
          <div className="flex flex-col md:flex-row gap-4 md:gap-10 justify-between py-8 md:py-20">
            <Skeleton className={cn(inputBackgroundColor, "h-14 w-full md:w-70")} />
            <Skeleton className={cn(inputBackgroundColor, "h-14 w-full md:w-80")} />   
          </div>
          <div className="">
            <div className="grid gap-x-6 gap-y-8 md:gap-y-20 md:grid-cols-2">
              {[...Array(6)].map((_, index) => (
                <React.Fragment key={index}>
                   <Card
                    key={index}
                    className="grid grid-rows-[auto_auto_1fr_auto] p-0 border-none gap-4"
                  >
                    <div className="aspect-16/9 w-full pb-2">
                      <Skeleton className="h-full w-full" />
                    </div>
                    <CardHeader className="!p-0 gap-0">
                      <Skeleton className={cn(inputBackgroundColor, "h-8 w-[100%] mb-2")} />   
                      <Skeleton className={cn(inputBackgroundColor, "h-8 w-[80%]")} />   
                    </CardHeader>
                    <CardContent className="!p-0">
                      <Skeleton className={cn(inputBackgroundColor, "h-5 w-[100%] mb-2")} />   
                      <Skeleton className={cn(inputBackgroundColor, "h-5 w-[100%]")} />   
                    </CardContent>
                    <CardFooter className="p-0 flex flex-wrap gap-2">
                      <Skeleton className={cn(inputBackgroundColor, "h-[30px] w-50")} />
                    </CardFooter>
                  </Card>
                </React.Fragment>
              ))}
            </div>
            <div className="mt-8 md:mt-20">
              <Skeleton className={cn(inputBackgroundColor, "h-14 w-50 rounded-full hidden lg:block m-auto")} />   
            </div>
          </div>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="case-studies-list"
      attributes={attributes}
    >
      <div className="container" data-aos="fade-up">
        <h2>
          Latest insights.
        </h2>
        <Controls
          selectedTopic={selectedTopic}
          onTopicChange={handleTopicChange}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          topicsData={topicsData}
          inputBackgroundColor={inputBackgroundColor}
        />
        <div className="">
          <ArticleList
            items={fetchedItems}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isSearching={isSearching}
            onLoadMore={handleLoadMore}
            onReset={handleReset}
          />
        </div>
      </div>
    </SectionWrapper>
  );
} 